import React, { useCallback, useContext, useMemo, useState } from 'react';

import { useSearchParams } from 'react-router-dom';

import { Alert, Box, Button, CircularProgress, MenuItem, Stack, TextField, Typography } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';

import { useGetServiceCostPrices, useGetTourServices } from '~/hooks/apis/tours/useTourCostPrices';
import { useGetTourDepartures } from '~/hooks/apis/tours/useTourDepartures';

import {
  FixedByDepartureCostPriceItem,
  FixedCostPriceItem,
  SlidingScaleByDeparturePricingItem,
  SlidingScalePricingItem,
  TourService,
} from '~/types/tours/costPrice';
import { costPriceTypeLabel, servicePricing } from '~/types/tours/supplier';

import LinkSupplierService from '../../Suppliers/LinkSupplierService';
import TourDetailsContext from '../TourDetailsContext';

import DepartureCostPrices from './DepartureCostPrices';
import FixedPriceDialog from './FixedPriceDialog';
import FixedPriceTable from './FixedPriceTable';
import SlidingScaleTable from './SlidingScaleTable';
import SlidingScaleUploadDialog from './SlidingScaleUploadDialog';
import { DepartureItem, buildDepartureRows } from './departureRows';

interface Props {
  tourId: string;
}

/** How a service is costed, since it decides what can be priced against it. */
function describeCosting(service: TourService | null): string {
  if (!service?.costPriceType) {
    return "Cost prices belong to a supplier's service on this tour";
  }

  const scope = service.costPriceScope === 'Departure' ? 'per departure' : 'for the whole tour';

  return `Costed as ${costPriceTypeLabel(service.costPriceType).toLowerCase()}, priced ${scope}`;
}

function Loading() {
  return (
    <Box p={4} display="flex" justifyContent="center">
      <CircularProgress />
    </Box>
  );
}

const SERVICE_ID_PARAM = 'serviceId';

export default function TourSlidingScale({ tourId }: Props) {
  const { tourDetails } = useContext(TourDetailsContext);

  const [searchParams, setSearchParams] = useSearchParams();

  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isFixedPriceOpen, setIsFixedPriceOpen] = useState(false);

  /** Set when an upload was started from one departure's row. */
  const [uploadDeparture, setUploadDeparture] = useState<string | null>(null);

  const [isLinking, setIsLinking] = useState(false);

  const { data: services = [], isLoading: isLoadingServices, error: servicesError } = useGetTourServices(tourId);

  /**
   * The chosen service is kept in the URL so the view can be linked and
   * survives a reload. It defaults to the tour's only service when there is
   * just one, which is the common case.
   */
  const selectedServiceId = searchParams.get(SERVICE_ID_PARAM) ?? (services.length === 1 ? services[0].id : '');

  const handleServiceChange = useCallback(
    (serviceId: string) => {
      const next = new URLSearchParams(searchParams);

      if (serviceId) {
        next.set(SERVICE_ID_PARAM, serviceId);
      } else {
        next.delete(SERVICE_ID_PARAM);
      }

      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const selectedService = useMemo(
    () => services.find((service) => service.id === selectedServiceId) ?? null,
    [services, selectedServiceId],
  );

  // Cost prices are keyed by supplier and service, both taken from the
  // selected service.
  const serviceIdentifier = useMemo(
    () => ({ supplierId: selectedService?.supplierId, serviceId: selectedService?.id }),
    [selectedService],
  );

  const { data, isLoading, error } = useGetServiceCostPrices(serviceIdentifier);

  /**
   * svc-tour returns one of four shapes, whichever matches how the service is
   * costed, so each is read out once and the rest stay empty.
   */
  const { pricing, fixedPrices, byDeparturePricing, fixedByDeparturePrices } = useMemo(
    () => ({
      pricing: data && 'slidingScale' in data ? data.slidingScale : ([] as Array<SlidingScalePricingItem>),
      fixedPrices: data && 'fixed' in data ? data.fixed : ([] as Array<FixedCostPriceItem>),
      byDeparturePricing:
        data && 'slidingScaleByDeparture' in data
          ? data.slidingScaleByDeparture
          : ([] as Array<SlidingScaleByDeparturePricingItem>),
      fixedByDeparturePrices:
        data && 'fixedByDeparture' in data ? data.fixedByDeparture : ([] as Array<FixedByDepartureCostPriceItem>),
    }),
    [data],
  );

  // With no service there is nothing to price, so those actions are left out
  // rather than shown disabled.
  const hasService = !!selectedService;

  const pricingActions = selectedService ? servicePricing(selectedService) : null;
  const isByDeparture = !!pricingActions?.isByDeparture;

  /**
   * A fixed service holds only fixed rows, so its own prices are what the
   * dialog opens on: the currency it quotes in, and the seasons it already
   * prices, so setting one more does not introduce "low" beside "Low".
   */
  const fixedForKind = pricingActions?.kind === 'FixedByDeparture' ? fixedByDeparturePrices : fixedPrices;

  const fixedCurrency = fixedForKind[0]?.currency;

  const seasonOptions = useMemo(
    () => Array.from(new Set(fixedForKind.map((item) => item.season))).sort(),
    [fixedForKind],
  );

  const {
    data: tourDepartures = [],
    isLoading: isLoadingDepartures,
    error: departuresError,
  } = useGetTourDepartures(isByDeparture ? tourId : undefined);

  /** A by-departure service holds one shape or the other, never both. */
  const departureRows = useMemo(() => {
    const items: Array<DepartureItem> =
      pricingActions?.kind === 'FixedByDeparture' ? fixedByDeparturePrices : byDeparturePricing;

    return buildDepartureRows(tourDepartures, items);
  }, [byDeparturePricing, fixedByDeparturePrices, pricingActions, tourDepartures]);

  return (
    <Stack spacing={2} sx={{ minWidth: 0, maxWidth: '100%' }}>
      <PageHeader title="Sliding scale">
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {hasService ? (
            <>
              {/* A per-departure service is priced from its departure's own
                  row, where there is a departure to price. */}
              {!isByDeparture && pricingActions.canUploadSlidingScale && (
                <Button variant="contained" onClick={() => setIsUploadOpen(true)}>
                  {pricing.length ? 'Re-upload sliding scale' : 'Upload sliding scale'}
                </Button>
              )}
              {!isByDeparture && pricingActions.canSetFixedPrice && (
                <Button variant="contained" onClick={() => setIsFixedPriceOpen(true)}>
                  Set fixed price
                </Button>
              )}
              <Button variant="text" onClick={() => setIsLinking(true)}>
                Link another service
              </Button>
            </>
          ) : (
            <Button variant="contained" onClick={() => setIsLinking(true)}>
              Link supplier &amp; service
            </Button>
          )}
        </Stack>
      </PageHeader>

      {isLoadingServices && <Loading />}

      {!!servicesError && <Alert severity="error">{servicesError.message}</Alert>}

      {!isLoadingServices && !servicesError && !services.length && (
        <Alert severity="info">
          This tour has no supplier services yet, so there is nowhere to attach a cost price. Link a supplier and create
          a service for the tour to start pricing it.
        </Alert>
      )}

      {!!services.length && (
        <TextField
          select
          label="Service"
          value={selectedServiceId}
          onChange={(event) => handleServiceChange(event.target.value)}
          helperText={describeCosting(selectedService)}
          sx={{ maxWidth: 560 }}
        >
          {services.map((service) => (
            <MenuItem key={service.id} value={service.id}>
              {service.name} — {service.kind}
            </MenuItem>
          ))}
        </TextField>
      )}

      {!!selectedService && isLoading && <Loading />}

      {!!selectedService && !!error && <Alert severity="error">{error.message}</Alert>}

      {!!selectedService && !isLoading && !error && (
        <Stack spacing={1} sx={{ minWidth: 0 }}>
          <Typography variant="h4" fontWeight={600}>
            Cost prices
          </Typography>

          {isByDeparture ? (
            <>
              {isLoadingDepartures && <Loading />}

              {/* Without the tour's departures there is nothing to price
                  against, so a failure is reported rather than read as a tour
                  that runs none. */}
              {!!departuresError && (
                <Alert severity="error">Could not load this tour&apos;s departures: {departuresError.message}</Alert>
              )}

              {!isLoadingDepartures && !departuresError && (
                <DepartureCostPrices
                  rows={departureRows}
                  kind={pricingActions.kind}
                  onUpload={(departure) => {
                    setUploadDeparture(departure);

                    if (pricingActions.kind === 'FixedByDeparture') {
                      setIsFixedPriceOpen(true);
                      return;
                    }

                    setIsUploadOpen(true);
                  }}
                />
              )}
            </>
          ) : (
            /* A service is costed one way, so only that table is worth
               showing: the other's empty state describes a scale or a rate this
               service will never hold. */
            <>
              {pricingActions.kind === 'Fixed' ? (
                <FixedPriceTable prices={fixedPrices} />
              ) : (
                <SlidingScaleTable pricing={pricing} />
              )}
            </>
          )}
        </Stack>
      )}

      {isUploadOpen && !!selectedService && (
        <SlidingScaleUploadDialog
          open={isUploadOpen}
          service={selectedService}
          existingRowCount={isByDeparture ? byDeparturePricing.length : pricing.length}
          byDeparture={isByDeparture}
          tourId={tourId}
          // Clearing the dialog's own picker is what asks for a sheet spanning
          // several departures.
          departures={isByDeparture && uploadDeparture ? [uploadDeparture] : undefined}
          existingItems={isByDeparture ? byDeparturePricing : undefined}
          onClose={() => {
            setIsUploadOpen(false);
            setUploadDeparture(null);
          }}
        />
      )}

      {isFixedPriceOpen && !!selectedService && (
        <FixedPriceDialog
          open={isFixedPriceOpen}
          service={selectedService}
          defaultCurrency={fixedCurrency}
          seasonOptions={seasonOptions}
          byDeparture={isByDeparture}
          departure={tourDepartures.find((option) => option.departureId === uploadDeparture)}
          existingByDeparturePrices={fixedByDeparturePrices}
          onClose={() => {
            setIsFixedPriceOpen(false);
            setUploadDeparture(null);
          }}
        />
      )}

      {isLinking && (
        <LinkSupplierService
          tour={{ id: tourId, name: tourDetails?.title ?? tourId }}
          onClose={() => setIsLinking(false)}
          onCreated={(service) => {
            setIsLinking(false);
            handleServiceChange(service.id);
          }}
        />
      )}
    </Stack>
  );
}
